/**
 * M10 P4b7a perf scene (plan `docs/plans/2026-10-15-m10-p4b.md` §3.3 P4b7a; hull tiers spec §6.6 / §7 P4b7): 40 real
 * ships in view at the Hub, the four real L blocks (Gannet, Warhound, Skua, Danube) moored at its ring with the Hub
 * as the XL stand-in, and one L close on the chase camera. `dev.json perfScene` holds the scene, so the headless
 * check (`tests/e2e/ship-perf.spec.ts`, which stages it in phases through `__debug`) and the user's optional F3
 * reading (`?perfScene=p4b7` in a dev build, staged at once by `app/debug.ts stagePerfScene`) see the same ships
 * from the same vantage (Q7: the reading confirms, it doesn't block).
 *
 * Pure: the scene schema, the URL switch, the on-screen note and the asserted verdict (so the asserts are unit
 * tested, not only exercised by one browser run).
 */
import { z } from 'zod'

/** The near pose's range on the chase camera (card: one L at 300–600 m). */
const NEAR_MIN_M = 300
const NEAR_MAX_M = 600
/** M10 P4b7b: the fewest warm interleaved windows per arm (card and D-953). */
const MIN_WINDOWS_PER_ARM = 6

/**
 * M10 P4b7b (`tests/e2e/ship-perf-gpu.spec.ts`): the warm interleaved L vs S/M GPU pairs. Each pose (ring, near)
 * alternates the scene with its L and the same scene with each L swapped for an S/M def at the same pose; a window
 * settles `settleFrames`, then reads the F3 GPU line (`gpuTiming.ts`) after `measureFrames` more. The bar (plan Q7):
 * the L arm's median scene-pass GPU ms ≤ the S/M arm's + max(`maxRisePct` %, `maxRiseMs`) [P].
 */
export const perfGpuPairSchema = z.object({
  /** The S/M def that stands in for each ring L (same order as `ring`), and for the near L. */
  ringSwap: z.array(z.string().min(1)).min(1),
  nearSwap: z.string().min(1),
  /** Measured windows per arm, after `warmupPairs` discarded L/S/M pairs. */
  windowsPerArm: z.number().int().min(MIN_WINDOWS_PER_ARM),
  warmupPairs: z.number().int().min(1),
  /** Frames after a swap before the window's reading, and frames the reading covers. */
  settleFrames: z.number().int().positive(),
  measureFrames: z.number().int().positive(),
  /** [P] the bar: the L median may exceed the S/M median by max(this % of it, `maxRiseMs`). */
  maxRisePct: z.number().positive(),
  maxRiseMs: z.number().positive(),
})

export const perfSceneConfigSchema = z
  .object({
    /** The dev switch: false and `?perfScene` does nothing (dev builds only either way). */
    enabled: z.boolean(),
    /** The `?perfScene=` value that stages this scene. */
    id: z.string().min(1),
    /** The lawful faction every spawned ship flies for, and its (frozen) pilot profile. */
    faction: z.string().min(1),
    profile: z.string().min(1),
    /** S/M ships in a line ahead of the vantage (every S/M def in turn): count, first range and spacing, m. */
    smallCount: z.number().int().positive(),
    smallFirstM: z.number().positive(),
    smallStepM: z.number().positive(),
    /** The L defs moored at the Hub's ring, one per slot in `ringSlots` (mooring slots of `moor.json`). */
    ring: z.array(z.string().min(1)).min(1),
    ringSlots: z.array(z.number().int().nonnegative()).min(1),
    /** The L def staged close on the chase camera, and how far ahead of the player, m. */
    near: z.string().min(1),
    nearM: z.number().min(NEAR_MIN_M).max(NEAR_MAX_M),
    /** Where the player looks at the Hub from (`lookAtPose`: range from the hull, azimuth and elevation off the bay axis). */
    vantage: z.object({ rangeFromHullM: z.number().positive(), azDeg: z.number(), elDeg: z.number().gt(-90).lt(90) }),
    /** M10 P4b7b: the GPU pairs (`perfGpuPairSchema`). */
    gpu: perfGpuPairSchema,
  })
  .refine((c) => c.ring.length === c.ringSlots.length, { message: 'perfScene: one ring slot per ring def' })
  .refine((c) => new Set(c.ringSlots).size === c.ringSlots.length, { message: 'perfScene: ring slots must differ' })
  .refine((c) => c.gpu.ringSwap.length === c.ring.length, { message: 'perfScene: one swap def per ring def' })

export type PerfSceneConfig = z.infer<typeof perfSceneConfigSchema>

/** The scene `?perfScene=<id>` asks for, or null (no parameter, another id, or the dev switch off). */
export function perfSceneRequested(search: string, cfg: PerfSceneConfig): PerfSceneConfig | null {
  const id = new URLSearchParams(search).get('perfScene')
  return id !== null && cfg.enabled && id === cfg.id ? cfg : null
}

/** Ships the scene spawns: the S/M line, the ring and the near L. */
export function perfSceneShips(cfg: PerfSceneConfig): number {
  return cfg.smallCount + cfg.ring.length + 1
}

/** The one-line on-screen note while the scene is staged. */
export function perfSceneNote(cfg: PerfSceneConfig): string {
  return (
    `perfScene ${cfg.id}: ${cfg.smallCount} S/M + ${cfg.ring.length} L at the Hub ring + 1 L at ${cfg.nearM} m ` +
    `(${perfSceneShips(cfg)} ships, the Hub as the XL stand-in); AI frozen, invulnerable. F3 for the reading.`
  )
}

/** What the headless check measured (medians of whole-frame draws per phase, the D-571 counter around first sight). */
export interface ShipPerfSample {
  /** Draws before any spawn, with the S/M in view, and with every ship in view. */
  readonly drawsBefore: number
  readonly drawsSmall: number
  readonly drawsAll: number
  readonly smallCount: number
  readonly largeCount: number
  /** `pipelinesBuilt` just before the L first came into view, and at the end of the window after. */
  readonly pipelinesBefore: number
  readonly pipelinesAfter: number
}

/** The asserted limits: `passBudgets.json ships.perShipDrawsSM` and the L factor (P1f ruling, 1.25 [P]). */
export interface ShipPerfLimits {
  readonly perShipDrawsSM: number
  readonly lFactor: number
}

export interface ShipPerfVerdict {
  readonly smallPerShip: number
  readonly largePerShip: number
  readonly pipelinesOnSight: number
  /** One line per failed assert; empty when the scene passes. */
  readonly failures: readonly string[]
}

/**
 * The P4b7a asserts: S/M draws per ship within `perShipDrawsSM`; the draws an L adds per ship at most `lFactor` × an
 * S/M's; no pipeline built when the L first show (D-534). Whole-frame totals and the frame p95 are logged by the
 * spec, never judged here.
 */
export function shipPerfVerdict(s: ShipPerfSample, lim: ShipPerfLimits): ShipPerfVerdict {
  const smallPerShip = (s.drawsSmall - s.drawsBefore) / s.smallCount
  const largePerShip = (s.drawsAll - s.drawsSmall) / s.largeCount
  const pipelinesOnSight = s.pipelinesAfter - s.pipelinesBefore
  const failures: string[] = []
  if (!(smallPerShip <= lim.perShipDrawsSM)) failures.push(`S/M draws per ship ${smallPerShip.toFixed(2)} > perShipDrawsSM ${lim.perShipDrawsSM}`)
  const lCap = smallPerShip * lim.lFactor
  if (!(largePerShip <= lCap)) failures.push(`L draws per ship ${largePerShip.toFixed(2)} > ${lim.lFactor} × S/M = ${lCap.toFixed(2)}`)
  if (pipelinesOnSight !== 0) failures.push(`${pipelinesOnSight} pipeline(s) built when the L first came into view`)
  return { smallPerShip, largePerShip, pipelinesOnSight, failures }
}

/** The median (the mean of the middle two for an even count; NaN when empty). */
export function medianOf(xs: readonly number[]): number {
  if (xs.length === 0) return Number.NaN
  const s = [...xs].sort((a, b) => a - b)
  const m = s.length >> 1
  return s.length % 2 === 1 ? s[m] : (s[m - 1] + s[m]) / 2
}

/** One pose and leg of the GPU check: the scene-pass GPU ms of each measured window, per arm. */
export interface GpuPairSample {
  /** The F3 readout had `timestamp-query` (false: it read `gpu n/a`). */
  readonly timestampQuery: boolean
  readonly lSceneMs: readonly number[]
  readonly smSceneMs: readonly number[]
}

/** `perfScene.gpu`'s bar and window count. */
export interface GpuPairLimits {
  readonly maxRisePct: number
  readonly maxRiseMs: number
  readonly windowsPerArm: number
}

export interface GpuPairVerdict {
  readonly lMedian: number
  readonly smMedian: number
  /** L median − S/M median, and the highest L median that passes. */
  readonly riseMs: number
  readonly limitMs: number
  /** One line per failed assert; empty when the pair passes. */
  readonly failures: readonly string[]
}

function armFailures(name: string, ms: readonly number[], windows: number): string[] {
  const out: string[] = []
  if (ms.length < windows) out.push(`${name} arm: ${ms.length} window(s) < ${windows}`)
  const bad = ms.filter((x) => !(Number.isFinite(x) && x > 0)).length
  if (bad > 0) out.push(`${name} arm: ${bad} window(s) without a GPU reading`)
  return out
}

/**
 * The P4b7b assert (plan Q7, review M4, blind Q5): the L arm's median scene-pass GPU ms ≤ the S/M arm's median +
 * max(`maxRisePct` %, `maxRiseMs`). It never passes vacuously: no `timestamp-query`, a window without a reading or an
 * arm short of `windowsPerArm` windows fails. The span, `js render` and frame p95 are logged by the spec, never judged.
 */
export function gpuPairVerdict(s: GpuPairSample, lim: GpuPairLimits): GpuPairVerdict {
  const lMedian = medianOf(s.lSceneMs)
  const smMedian = medianOf(s.smSceneMs)
  const limitMs = smMedian + Math.max((smMedian * lim.maxRisePct) / 100, lim.maxRiseMs)
  const riseMs = lMedian - smMedian
  const failures: string[] = []
  if (!s.timestampQuery) failures.push('no timestamp-query: the GPU pass times are unavailable, so the check cannot run')
  failures.push(...armFailures('L', s.lSceneMs, lim.windowsPerArm), ...armFailures('S/M', s.smSceneMs, lim.windowsPerArm))
  if (failures.length === 0 && !(lMedian <= limitMs)) {
    failures.push(
      `L scene-pass median ${lMedian.toFixed(2)} ms > S/M ${smMedian.toFixed(2)} ms + max(${lim.maxRisePct} %, ${lim.maxRiseMs.toFixed(1)} ms) = ${limitMs.toFixed(2)} ms`,
    )
  }
  return { lMedian, smMedian, riseMs, limitMs, failures }
}

/** What the GPU check reads off the F3 overlay (`perfOverlay.ts formatPerf`, `gpuTiming.ts formatGpuLine`). */
export interface F3Reading {
  /** False when the line reads `gpu n/a (no timestamp-query)` or there is no GPU line at all. */
  readonly timestampQuery: boolean
  /** Chrome quantises the timestamps (no developer-features flag). */
  readonly quantised: boolean
  readonly frameMs: number
  readonly jsMs: number
  /** `js … (render …)`: the CPU submit share of the frame. */
  readonly jsRenderMs: number
  /** GPU ms per frame (NaN until a reading, or without `timestamp-query`): the pass sum, the span and each class. */
  readonly passesMs: number
  readonly spanMs: number
  readonly shadowMs: number
  readonly prepassMs: number
  readonly sceneMs: number
  readonly postMs: number
  readonly otherMs: number
}

const F3_FRAME = /^\d+ fps {2}(\d+\.\d+) ms\/frame/m
const F3_JS = /^js (\d+\.\d+) ms\/frame \(sim \d+\.\d+ · features \d+\.\d+ · render (\d+\.\d+) · other \d+\.\d+\)/m
const F3_GPU = /^gpu (\d+\.\d+) ms passes · (\d+\.\d+) ms span \(shadow (\d+\.\d+) · prepass (\d+\.\d+) · scene (\d+\.\d+) · post (\d+\.\d+) · other (\d+\.\d+)\)/m
const F3_GPU_MEASURING = /^gpu … ms \(measuring\)/m

function num(m: RegExpExecArray | null, i: number): number {
  const s = m?.[i]
  return s === undefined ? Number.NaN : Number(s)
}

/** Parses the F3 overlay text (pure; NaN for what it doesn't show). */
export function readF3(text: string): F3Reading {
  const frame = F3_FRAME.exec(text)
  const js = F3_JS.exec(text)
  const gpu = F3_GPU.exec(text)
  return {
    timestampQuery: gpu !== null || F3_GPU_MEASURING.test(text),
    quantised: text.includes('gpu ms quantised'),
    frameMs: num(frame, 1),
    jsMs: num(js, 1),
    jsRenderMs: num(js, 2),
    passesMs: num(gpu, 1),
    spanMs: num(gpu, 2),
    shadowMs: num(gpu, 3),
    prepassMs: num(gpu, 4),
    sceneMs: num(gpu, 5),
    postMs: num(gpu, 6),
    otherMs: num(gpu, 7),
  }
}
